import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import moment from "moment/moment";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import axios from ".././axios-interceptor";
import { decrypt } from "../utils";
import {
  base_url,
  formatMoney,
  AUDITOR_ROLE,
  OPPORTUNITY_STATUS_OPEN,
  OPPORTUNITY_STATUS_FULLY_FUNDED,
  OPPORTUNITY_STATUS_DISBURSING,
  OPPORTUNITY_STATUS_PROCESSED,
} from "./../utilities";

// Admin "Investor Breakdown" tab for a multi-investor (Fund Now) loan.
// Backend: loan-investor-subscriptions/get_breakdown, update_breakdown, reconcile_ledgers.
// Rules mirrored from breakdown.service.js (the backend stays the authority):
//  - only 'pledged' rows can change; each new amount must be > 0 (removal = withdrawal flow)
//  - the new total of active pledges must not exceed the loan amount (under-funding is allowed)
//  - a reason is mandatory; only changed rows are sent

const cents = (n) => Math.round(Number(n) * 100);

// update_breakdown holds edit_in_progress for the few seconds a healthy edit takes. Only treat the
// lock as stuck (and offer Reconcile) if it is still set when we look again after this delay.
const LOCK_RECHECK_MS = 8000;

const STATE_BADGE = {
  pledged: { label: "Pledged", bg: "#EEF2FF", color: "var(--ben-blue)", border: "#C7D4FB" },
  disbursed: { label: "Disbursed", bg: "#E6EBF8", color: "var(--ben-navy)", border: "#C7D0EA" },
  withdrawal_requested: { label: "Withdrawal pending", bg: "#FAEEDA", color: "#854F0B", border: "#FAC775" },
  withdrawn: { label: "Withdrawn", bg: "#F3F3F1", color: "#7C8495", border: "#E5E7EB" },
};

const OPPORTUNITY_STATUS_LABEL = {
  [OPPORTUNITY_STATUS_OPEN]: "Open for funding",
  [OPPORTUNITY_STATUS_FULLY_FUNDED]: "Fully funded",
  [OPPORTUNITY_STATUS_DISBURSING]: "Disbursing",
  [OPPORTUNITY_STATUS_PROCESSED]: "Disbursed",
};

const pill = (style) => ({
  display: "inline-block",
  fontSize: 11,
  fontWeight: 700,
  padding: "3px 8px",
  borderRadius: 5,
  whiteSpace: "nowrap",
  ...style,
});

const InvestorBreakdownTab = ({ investorRequestId, isLoanClosed, roleCode, onChanged, onLoaded }) => {
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState({});
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [saveNote, setSaveNote] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  // subscription_ids the admin actually typed into; only these are ever sent as changes.
  const [editedIds, setEditedIds] = useState(() => new Set());
  // true once edit_in_progress has survived a delayed recheck (likely a stuck lock, not a live edit).
  const [lockStale, setLockStale] = useState(false);
  const editedIdsRef = useRef(editedIds);
  editedIdsRef.current = editedIds;
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  // Admin login never sets role_code (only the auditor/business/investor logins do), so admin status
  // comes from the admin session token, minus auditors (who also hold User_Accesstoken_Admin).
  // The cookie is decrypted here synchronously so an auditor never sees write actions on the first
  // render (the parent's roleCode prop is still the encrypted cookie until its effect runs).
  const [cookieRole] = useState(() => {
    const stored = Cookies.get("role_code");
    return stored ? decrypt(stored) : null;
  });
  const isAuditor = Number(cookieRole) === AUDITOR_ROLE || Number(roleCode) === AUDITOR_ROLE;
  const isAdmin = !!Cookies.get("User_Accesstoken_Admin") && !isAuditor;

  const fetchBreakdown = useCallback(
    ({ silent = false } = {}) => {
      if (!investorRequestId) return Promise.resolve(null);
      if (!silent) setLoading(true);
      return axios
        .post(base_url + "loan-investor-subscriptions/get_breakdown", { investor_request_id: investorRequestId })
        .then((res) => {
          if (res?.data?.status === 200 && res.data.data) {
            setBreakdown(res.data.data);
            setLoadFailed(false);
            return res.data.data;
          }
          if (!silent) setLoadFailed(true);
          return null;
        })
        .catch((err) => {
          console.log(err);
          if (!silent) setLoadFailed(true);
          return null;
        })
        .finally(() => {
          if (!silent) setLoading(false);
        });
    },
    [investorRequestId]
  );

  useEffect(() => {
    setBreakdown(null);
    setEditMode(false);
    setDraft({});
    setReason("");
    setSaveNote("");
    setExpandedId(null);
    setEditedIds(new Set());
    setLockStale(false);
    fetchBreakdown();
  }, [fetchBreakdown]);

  useEffect(() => {
    if (breakdown && onLoadedRef.current) onLoadedRef.current(breakdown);
  }, [breakdown]);

  // When fresh server data arrives mid-edit (e.g. after a failed save), rows the admin did not touch
  // follow the server amount, and newly appeared pledged rows get a seed value. Otherwise an untouched
  // stale draft would look like a change and silently overwrite another admin's edit on retry.
  useEffect(() => {
    if (!editMode || !breakdown) return;
    setDraft((d) => {
      const next = { ...d };
      (breakdown.investors || []).forEach((r) => {
        if (r.state === "pledged" && !editedIdsRef.current.has(r.subscription_id)) {
          next[r.subscription_id] = String(r.subscribed_amount);
        }
      });
      return next;
    });
    // editedIds is read through a ref on purpose: only a new breakdown should trigger a resync.
  }, [breakdown, editMode]);

  const lockSet = !!breakdown?.edit_in_progress;
  useEffect(() => {
    if (!lockSet) {
      setLockStale(false);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      fetchBreakdown({ silent: true }).then((d) => {
        if (!cancelled && d?.edit_in_progress) setLockStale(true);
      });
    }, LOCK_RECHECK_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [lockSet, fetchBreakdown]);

  const investors = useMemo(() => breakdown?.investors || [], [breakdown]);
  const currency = breakdown?.currency;
  const target = Number(breakdown?.target_amount || 0);
  const ledgerMismatch = !!breakdown && (breakdown.counter_in_sync === false || breakdown.pledged_in_sync === false);
  const outOfSync = ledgerMismatch || (lockSet && lockStale);
  // QA #47: investor amounts are no longer editable from the breakdown tab (the backend refuses update_breakdown).

  const amountFor = (row) => {
    if (editMode && row.state === "pledged" && draft[row.subscription_id] !== undefined) {
      return draft[row.subscription_id];
    }
    return row.subscribed_amount;
  };

  const activeRows = investors.filter((r) => r.state !== "withdrawn");
  const total = activeRows.reduce((sum, r) => sum + (Number(amountFor(r)) || 0), 0);
  const totalCents = cents(total);
  const targetCents = cents(target);

  const changes = useMemo(() => {
    if (!editMode) return [];
    return investors
      .filter((r) => r.state === "pledged" && editedIds.has(r.subscription_id) && draft[r.subscription_id] !== undefined)
      .filter((r) => String(draft[r.subscription_id]).trim() !== "" && cents(draft[r.subscription_id]) !== cents(r.subscribed_amount))
      .map((r) => ({ subscription_id: r.subscription_id, subscribed_amount: Number(draft[r.subscription_id]) }));
  }, [editMode, investors, draft, editedIds]);

  const invalidRowIds = editMode
    ? investors
        .filter((r) => r.state === "pledged")
        .filter((r) => {
          const v = draft[r.subscription_id];
          const n = Number(v);
          return v === undefined || String(v).trim() === "" || !Number.isFinite(n) || !(n > 0);
        })
        .map((r) => r.subscription_id)
    : [];

  let validationError = "";
  if (editMode) {
    if (invalidRowIds.length > 0) validationError = "Each pledge amount must be greater than 0 (use the withdrawal flow to remove an investor).";
    else if (totalCents > targetCents) validationError = `Total exceeds the loan amount by ${formatMoney((totalCents - targetCents) / 100, currency)}.`;
    else if (changes.length === 0) validationError = "No amounts changed yet.";
    else if (!reason.trim()) validationError = "A reason is required for a breakdown change.";
  }
  const saveDisabled = saving || !!validationError;

  const cancelEdit = () => {
    setEditMode(false);
    setDraft({});
    setEditedIds(new Set());
    setReason("");
  };

  const handleSave = () => {
    if (saveDisabled) return;
    setSaving(true);
    const changeCount = changes.length;
    axios
      .post(base_url + "loan-investor-subscriptions/update_breakdown", {
        investor_request_id: investorRequestId,
        changes,
        reason: reason.trim(),
      })
      .then((res) => {
        if (res?.data?.status === 200) {
          toast.success("Investor breakdown updated", { toastId: 230 });
          if (res.data.data?.breakdown) setBreakdown(res.data.data.breakdown);
          setEditMode(false);
          setDraft({});
          setEditedIds(new Set());
          setReason("");
          setSaveNote(
            `Saved — ${changeCount} pledge${changeCount === 1 ? "" : "s"} updated. Logged to change history (admin, timestamp, before/after amounts, reason).`
          );
          fetchBreakdown({ silent: true });
          if (onChanged) onChanged();
        } else {
          // Interceptor already toasted the backend message (e.g. 409 balance / concurrent edit).
          // Stay in edit mode and pick up the latest server state without discarding the draft.
          fetchBreakdown({ silent: true });
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setSaving(false));
  };

  const handleReconcile = () => {
    if (reconciling) return;
    if (
      lockSet &&
      !window.confirm(
        "An edit lock is set on this opportunity. If another admin is still saving a breakdown change, reconciling now can break it. Reconcile anyway?"
      )
    ) {
      return;
    }
    setReconciling(true);
    axios
      .post(base_url + "loan-investor-subscriptions/reconcile_ledgers", { investor_request_id: investorRequestId })
      .then((res) => {
        if (res?.data?.status === 200) {
          const d = res.data.data || {};
          setLockStale(false);
          toast.success(
            `Ledgers reconciled — funded ${formatMoney(d.funded_before, currency)} → ${formatMoney(d.funded_after, currency)}`,
            { toastId: 231 }
          );
          fetchBreakdown({ silent: true });
          if (onChanged) onChanged();
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setReconciling(false));
  };

  if (loading && !breakdown) {
    return (
      <div className="d-flex justify-content-center mt-3">
        <ThreeDots height="40" width="40" radius="9" color="#3D79F3" ariaLabel="three-dots-loading" visible={true} />
      </div>
    );
  }

  if (!breakdown) {
    return (
      <div className="p-3 text-center" style={{ fontSize: 13, color: "#7C8495" }}>
        {loadFailed ? "Could not load the investor breakdown." : "No breakdown available."}
        {loadFailed && (
          <div className="mt-2">
            <button className="btn btn_background px-3 py-1" style={{ fontSize: 13 }} onClick={() => fetchBreakdown()}>
              Retry
            </button>
          </div>
        )}
      </div>
    );
  }

  let statusChip;
  if (totalCents === targetCents) {
    statusChip = (
      <span style={pill({ background: "#EAF3DE", color: "#3B6D11" })}>Matches loan amount ({formatMoney(target, currency)})</span>
    );
  } else if (totalCents > targetCents) {
    statusChip = (
      <span style={pill({ background: "#FCEBEB", color: "#A32D2D" })}>
        Over by {formatMoney((totalCents - targetCents) / 100, currency)} vs loan amount {formatMoney(target, currency)}
      </span>
    );
  } else {
    statusChip = (
      <span style={pill({ background: "#FAEEDA", color: "#854F0B" })}>
        {formatMoney((targetCents - totalCents) / 100, currency)} remaining of {formatMoney(target, currency)}
      </span>
    );
  }

  const activeInvestorCount = new Set(activeRows.map((r) => String(r.investor_id))).size;

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h6 className="mb-1" style={{ fontWeight: 700 }}>Subscription breakdown by investor</h6>
          <div style={{ fontSize: 12, color: "#7C8495" }}>
            {formatMoney(breakdown.funded, currency)} funded of {formatMoney(target, currency)} ({Number(breakdown.percentFunded || 0)}%)
            {" · "}
            {activeInvestorCount} investor{activeInvestorCount === 1 ? "" : "s"}
            {OPPORTUNITY_STATUS_LABEL[breakdown.status] ? ` · ${OPPORTUNITY_STATUS_LABEL[breakdown.status]}` : ""}
          </div>
        </div>
      </div>

      {lockSet && !lockStale && !ledgerMismatch && (
        <div className="p-2 mb-3" style={{ background: "#EEF2FF", color: "var(--ben-blue)", border: "1px solid #C7D4FB", borderRadius: 8, fontSize: 12.5 }}>
          Another admin is saving a breakdown change on this opportunity. Editing is paused until it finishes.
        </div>
      )}

      {outOfSync && (
        <div
          className="p-3 mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2"
          style={{ background: "#FAEEDA", color: "#854F0B", border: "1px solid #FAC775", borderRadius: 8, fontSize: 12.5 }}
        >
          <div>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>Funding ledgers need attention</div>
            {breakdown.counter_in_sync === false && (
              <div>
                Funded counter ({formatMoney(breakdown.counter_funded_amount, currency)}) is out of step with the pledges (
                {formatMoney(breakdown.funded, currency)}).
              </div>
            )}
            {breakdown.pledged_in_sync === false && <div>One or more investors' reserved (pledged) balance does not match their open pledges.</div>}
            {lockSet && lockStale && (
              <div>
                An edit lock has been set on this opportunity for a while. A previous edit may not have completed, or another admin may
                still be saving one — check before reconciling.
              </div>
            )}
          </div>
          {isAdmin && !isLoanClosed && (
            <button
              className="btn blue_btn px-3 py-1"
              style={{ fontSize: 13 }}
              onClick={handleReconcile}
              disabled={reconciling || saving}
            >
              {reconciling ? "Reconciling..." : "Reconcile"}
            </button>
          )}
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table className="table table-striped" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th>Investor</th>
              <th>Ref</th>
              <th>Committed ({currency})</th>
              <th>Share</th>
              <th>State</th>
              <th>History</th>
            </tr>
          </thead>
          <tbody>
            {investors.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center" style={{ color: "#7C8495" }}>
                  No investors have funded this opportunity yet
                </td>
              </tr>
            )}
            {investors.map((row) => {
              const badge = STATE_BADGE[row.state] || STATE_BADGE.pledged;
              const editableRow = editMode && row.state === "pledged";
              const withdrawn = row.state === "withdrawn";
              const amt = amountFor(row);
              const share = editableRow
                ? target > 0
                  ? Math.round(((Number(amt) || 0) / target) * 10000) / 100
                  : 0
                : Number(row.share_percent || 0);
              const adjustments = row.adjustments || [];
              const expanded = expandedId === row.subscription_id;
              const invalid = invalidRowIds.includes(row.subscription_id);
              return (
                <React.Fragment key={row.subscription_id}>
                  <tr style={withdrawn ? { color: "#7C8495" } : undefined}>
                    <td>{row.investor_name || "-"}</td>
                    <td>{row.investor_reference_no || "-"}</td>
                    <td style={{ minWidth: 140 }}>
                      {editableRow ? (
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          size="sm"
                          value={draft[row.subscription_id] ?? ""}
                          disabled={saving}
                          isInvalid={invalid}
                          onChange={(e) => {
                            const v = e.target.value;
                            setDraft((d) => ({ ...d, [row.subscription_id]: v }));
                            setEditedIds((ids) => (ids.has(row.subscription_id) ? ids : new Set(ids).add(row.subscription_id)));
                          }}
                        />
                      ) : (
                        <span style={withdrawn ? { textDecoration: "line-through" } : undefined}>
                          {formatMoney(row.subscribed_amount, row.currency || currency)}
                        </span>
                      )}
                      {row.state === "disbursed" && Number(row.disbursed_amount) > 0 && cents(row.disbursed_amount) !== cents(row.subscribed_amount) && (
                        <div style={{ fontSize: 11, color: "#7C8495" }}>Disbursed {formatMoney(row.disbursed_amount, currency)}</div>
                      )}
                    </td>
                    <td>{withdrawn ? "-" : `${share}%`}</td>
                    <td>
                      <span style={pill({ background: badge.bg, color: badge.color, border: `1px solid ${badge.border}` })}>{badge.label}</span>
                    </td>
                    <td>
                      {adjustments.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => setExpandedId(expanded ? null : row.subscription_id)}
                          style={{ background: "none", border: "none", padding: 0, color: "var(--ben-blue)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                        >
                          {adjustments.length} change{adjustments.length === 1 ? "" : "s"} {expanded ? "▴" : "▾"}
                        </button>
                      ) : (
                        <span style={{ fontSize: 12, color: "#7C8495" }}>-</span>
                      )}
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={6} style={{ background: "#FAFAF9" }}>
                        {[...adjustments]
                          .sort((a, b) => Number(b.adjusted_date_time || 0) - Number(a.adjusted_date_time || 0))
                          .map((a, i) => (
                            <div
                              key={a.revision_id || i}
                              className="d-flex flex-wrap gap-3"
                              style={{ fontSize: 12, padding: "4px 0", borderBottom: i < adjustments.length - 1 ? "1px solid #E5E7EB" : "none" }}
                            >
                              <span style={{ color: "#7C8495", minWidth: 130 }}>
                                {a.adjusted_date_time ? moment(Number(a.adjusted_date_time)).format("DD MMM YYYY, HH:mm") : "-"}
                              </span>
                              <span style={{ fontWeight: 600 }}>
                                {formatMoney(a.previous_amount, currency)} → {formatMoney(a.new_amount, currency)}
                              </span>
                              <span>by {a.adjusted_by || "-"}</span>
                              <span style={{ color: "#7C8495", flex: 1, minWidth: 160 }}>Reason: {a.reason || "-"}</span>
                            </div>
                          ))}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {investors.length > 0 && (
              <tr style={{ fontWeight: 700 }}>
                <td colSpan={2} style={{ borderTop: "1px solid #E5E7EB" }}>Total</td>
                <td style={{ borderTop: "1px solid #E5E7EB" }}>{formatMoney(total, currency)}</td>
                <td style={{ borderTop: "1px solid #E5E7EB" }}>{target > 0 ? Math.round((total / target) * 10000) / 100 : 0}%</td>
                <td colSpan={2} style={{ borderTop: "1px solid #E5E7EB" }}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editMode && (
        <div className="mb-2">
          <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
            Reason for change <span style={{ color: "red" }}>*</span>
          </label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="e.g. investor asked to reduce their commitment before disbursement"
            value={reason}
            disabled={saving}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
        <div className="d-flex flex-column gap-1">
          {statusChip}
          {editMode && validationError && <span style={{ fontSize: 11.5, color: "#A32D2D" }}>{validationError}</span>}
        </div>
        <div className="d-flex align-items-center gap-2">
          {editMode && (
            <>
              <button className="btn btn_background px-3 py-1" style={{ fontSize: 13 }} onClick={cancelEdit} disabled={saving}>
                Cancel
              </button>
              <button
                className="btn blue_btn px-3 py-1"
                style={{ fontSize: 13, opacity: saveDisabled ? 0.5 : 1, cursor: saveDisabled ? "not-allowed" : "pointer" }}
                onClick={handleSave}
                disabled={saveDisabled}
              >
                {saving ? "Saving..." : "Save Breakdown"}
              </button>
            </>
          )}
        </div>
      </div>

      {saveNote && !editMode && <div style={{ fontSize: 11.5, color: "#059669", marginTop: 8 }}>{saveNote}</div>}

    </div>
  );
};

export default InvestorBreakdownTab;
